import { motion } from "framer-motion";
import { ArrowRight, Github, Linkedin, Mail } from "lucide-react";
import { profile } from "../../data/content";

const easing = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Ambient glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[80rem] h-[80rem] rounded-full opacity-[0.07]"
        style={{
          background:
            "radial-gradient(circle at center, #ffffff 0%, transparent 55%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 grain opacity-[0.04] mix-blend-screen"
      />

      <div className="relative max-w-6xl mx-auto px-6 md:px-12 w-full pt-32 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: easing }}
          className="inline-flex items-center gap-2.5 rounded-full border border-[var(--border-subtle)] bg-white/[0.03] px-3.5 py-1.5 mb-8"
          data-testid="hero-availability-badge"
        >
          <span className="relative flex h-2 w-2">
            <span
              className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping"
              style={{ backgroundColor: "var(--status-available)" }}
            />
            <span
              className="relative inline-flex rounded-full h-2 w-2"
              style={{ backgroundColor: "var(--status-available)" }}
            />
          </span>
          <span className="text-xs md:text-sm font-medium text-secondary-c">
            {profile.availability}
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: easing, delay: 0.05 }}
          className="font-heading font-extrabold tracking-tight text-primary-c text-4xl sm:text-6xl lg:text-7xl leading-[1.02] max-w-4xl"
          data-testid="hero-headline"
        >
          {profile.hero.headlineTop}
          <br />
          <span className="text-secondary-c">{profile.hero.headlineBottom}</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: easing, delay: 0.15 }}
          className="mt-8 max-w-2xl text-base md:text-lg text-secondary-c leading-relaxed"
          data-testid="hero-subtext"
        >
          {profile.hero.subtext}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: easing, delay: 0.25 }}
          className="mt-10 flex flex-wrap items-center gap-3"
        >
          <button
            data-testid="hero-view-work-btn"
            onClick={() => scrollTo("#projects")}
            className="group inline-flex items-center gap-2 bg-white text-black font-medium rounded-full px-6 py-3 hover:bg-neutral-200 transition-colors"
          >
            View selected work
            <ArrowRight
              className="w-4 h-4 transition-transform group-hover:translate-x-0.5"
              strokeWidth={2}
            />
          </button>
          <a
            data-testid="hero-resume-btn"
            href={profile.resume}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-[var(--border-strong)] text-primary-c font-medium rounded-full px-6 py-3 hover:bg-white/5 transition-colors"
          >
            Download résumé
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="mt-12 flex items-center gap-5"
        >
          {[
            { Icon: Github, href: profile.github, label: "github" },
            { Icon: Linkedin, href: profile.linkedin, label: "linkedin" },
            { Icon: Mail, href: `mailto:${profile.email}`, label: "email" },
          ].map(({ Icon, href, label }) => (
            <a
              key={label}
              data-testid={`hero-social-${label}`}
              href={href}
              target={label === "email" ? undefined : "_blank"}
              rel="noopener noreferrer"
              className="text-tertiary-c hover:text-primary-c transition-colors"
              aria-label={label}
            >
              <Icon className="w-5 h-5" strokeWidth={1.5} />
            </a>
          ))}
        </motion.div>

        {/* Now bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="mt-16 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-[var(--border-subtle)] pt-8"
        >
          <div>
            <p className="font-mono-tech text-[11px] tracking-[0.2em] uppercase text-tertiary-c">
              Currently
            </p>
            <p className="mt-1.5 text-sm text-primary-c">
              {profile.companyRole} · {profile.company}
            </p>
          </div>
          <div>
            <p className="font-mono-tech text-[11px] tracking-[0.2em] uppercase text-tertiary-c">
              Experience
            </p>
            <p className="mt-1.5 text-sm text-primary-c">
              {profile.yearsExperience} years
            </p>
          </div>
          <div>
            <p className="font-mono-tech text-[11px] tracking-[0.2em] uppercase text-tertiary-c">
              Focus
            </p>
            <p className="mt-1.5 text-sm text-primary-c">
              Identity &amp; RBAC · Multi-tenant SaaS
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
